import { Field, Select as BaseSelect } from '@base-ui/react';
import { FC, KeyboardEvent, memo, MouseEvent, useCallback, useMemo, useState } from 'react';

import SearchIcon from '@/assets/svg/ui-elements/search-lg.svg';

import Badge from '../Badge';
import Button from '../Button';
import Checkbox from '../Checkbox';
import HelperDescription from '../Helper/HelperDescription';
import Input from '../Input';
import Label from '../Label';
import Tooltip from '../Tooltip';
import CheckIcon from './assets/check.svg';
import ChevronDownIcon from './assets/chevron-down.svg';
import {
  StyledActionsRow,
  StyledBadgeWrapper,
  StyledCheckboxWrapper,
  StyledChevronIcon,
  StyledEmpty,
  StyledGroup,
  StyledGroupLabel,
  StyledGroupSeparator,
  StyledIconWrapper,
  StyledItem,
  StyledItemIconWrapper,
  StyledItemIndicator,
  StyledItemSupportText,
  StyledItemText,
  StyledItemTextRow,
  StyledList,
  StyledPopup,
  StyledPositioner,
  StyledRoot,
  StyledSearchWrapper,
  StyledSeparator,
  StyledTrigger,
  StyledValue,
  StyledValueText,
} from './Select.styled';
import type { SelectOption, SelectProps } from './types';

function getSelectedLabels(
  optionsByValue: Map<string, SelectOption>,
  value: string | string[] | null,
) {
  const values = Array.isArray(value) ? value : value ? [value] : [];
  return values
    .map((v) => optionsByValue.get(v)?.label)
    .filter((label): label is string => !!label);
}

interface SelectItemProps {
  option: SelectOption;
  isSelected: boolean;
  multiple: boolean;
  hidden: boolean;
}

const SelectItem = memo(({ option, isSelected, multiple, hidden }: SelectItemProps) => (
  <StyledItem
    value={option.value}
    disabled={option.disabled}
    style={hidden ? { display: 'none' } : undefined}
  >
    {option.icon && <StyledItemIconWrapper>{option.icon}</StyledItemIconWrapper>}
    <StyledItemTextRow>
      <StyledItemText>{option.label}</StyledItemText>
      {option.supportText && <StyledItemSupportText>{option.supportText}</StyledItemSupportText>}
    </StyledItemTextRow>
    {multiple ? (
      <StyledCheckboxWrapper>
        <Checkbox checked={isSelected} readOnly tabIndex={-1} size="md" />
      </StyledCheckboxWrapper>
    ) : (
      <StyledItemIndicator>
        <CheckIcon />
      </StyledItemIndicator>
    )}
  </StyledItem>
));
SelectItem.displayName = 'SelectItem';

const Select: FC<SelectProps> = ({
  options,
  value,
  defaultValue,
  onChange,
  placeholder = 'Select item',
  ariaLabel,
  size = 'md',
  multiple = false,
  disabled = false,
  required,
  optional,
  tooltip,
  topRightLabel,
  label,
  helperText,
  state = 'default',
  iconStart,
  startIcon,
  name,
  containerClassName,
  changed,
  showSearch = false,
  searchPlaceholder = 'Search',
  emptyMessage = 'No options available',
  noResultsMessage = 'No results found',
  showSelectAll = false,
  triggerLabel,
  popupWidth,
  groups = {},
  open,
  onOpenChange,
  trigger,
  align,
}) => {
  const [internalValue, setInternalValue] = useState<string | string[] | null>(
    defaultValue ?? (multiple ? [] : null),
  );
  const isValueControlled = value !== undefined;
  const currentValue = isValueControlled ? value : internalValue;

  const [internalOpen, setInternalOpen] = useState(false);
  const isOpenControlled = open !== undefined;
  const isOpen = isOpenControlled ? open : internalOpen;

  const [search, setSearch] = useState('');

  const optionsByValue = useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options],
  );

  // Bridge base-ui's onValueChange (which can pass null for single deselect) to our onChange
  const handleValueChange = useCallback(
    (val: string | string[] | null) => {
      if (!isValueControlled) setInternalValue(val);
      if (val !== null) (onChange as ((_v: string | string[]) => void) | undefined)?.(val);
    },
    [isValueControlled, onChange],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!isOpenControlled) setInternalOpen(nextOpen);
      onOpenChange?.(nextOpen);
      if (!nextOpen) setSearch('');
    },
    [isOpenControlled, onOpenChange],
  );

  // Compute which option values are hidden by search (not removed from DOM — just hidden)
  const hiddenValues = useMemo(() => {
    if (!showSearch || !search.trim()) return new Set<string>();
    const query = search.toLowerCase();
    const matches = (o: SelectOption) =>
      o.label.toLowerCase().includes(query) ||
      !!o.supportText?.toLowerCase().includes(query) ||
      !!o.searchText?.toLowerCase().includes(query);

    return new Set(options.filter((o) => !matches(o)).map((o) => o.value));
  }, [options, search, showSearch]);

  const hasVisibleOptions = options.length > hiddenValues.size;

  const selectedValues = useMemo(
    () => (Array.isArray(currentValue) ? currentValue : []),
    [currentValue],
  );
  const selectableValues = useMemo(
    () => options.filter((o) => !o.disabled).map((o) => o.value),
    [options],
  );
  const isAllSelected =
    selectableValues.length > 0 && selectableValues.every((v) => selectedValues.includes(v));
  // A disabled option cannot be toggled by hand, so Clear neither counts nor removes it.
  const isNoneSelected = !selectedValues.some((v) => selectableValues.includes(v));

  const handleSelectAll = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (isAllSelected) return;
      if (!isOpenControlled) setInternalOpen(true);
      handleValueChange(selectableValues);
    },
    [isAllSelected, isOpenControlled, selectableValues, handleValueChange],
  );

  const handleClear = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (isNoneSelected) return;
      if (!isOpenControlled) setInternalOpen(true);
      handleValueChange(selectedValues.filter((v) => !selectableValues.includes(v)));
    },
    [isNoneSelected, isOpenControlled, selectedValues, selectableValues, handleValueChange],
  );

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => e.stopPropagation();

  // Split into ungrouped and grouped buckets (always all options — search only hides via CSS)
  const ungroupedOptions = useMemo(() => options.filter((o) => !o.group), [options]);

  const groupedOptions = useMemo(() => {
    const map: Record<string, SelectOption[]> = {};
    for (const opt of options) {
      if (opt.group) {
        if (!map[opt.group]) map[opt.group] = [];
        map[opt.group].push(opt);
      }
    }
    return map;
  }, [options]);

  const groupKeys = useMemo(() => {
    const encountered = Object.keys(groupedOptions);
    const configured = Object.keys(groups).filter((k) => k in groupedOptions);
    const extras = encountered.filter((k) => !configured.includes(k));
    return [...configured, ...extras];
  }, [groupedOptions, groups]);

  const renderOption = (option: SelectOption) => (
    <SelectItem
      key={option.value}
      option={option}
      isSelected={selectedValues.includes(option.value)}
      multiple={multiple}
      hidden={hiddenValues.has(option.value)}
    />
  );

  return (
    <StyledRoot
      $inline={Boolean(trigger)}
      disabled={disabled}
      invalid={state === 'destructive'}
      className={containerClassName}
      data-changed={changed ? 'true' : undefined}
    >
      {label && (
        <Field.Label
          render={
            <Label
              required={required}
              optional={optional}
              tooltip={tooltip}
              topRightLabel={topRightLabel}
            >
              {label}
            </Label>
          }
        />
      )}
      <BaseSelect.Root<string, boolean>
        items={options}
        value={currentValue}
        onValueChange={handleValueChange}
        open={isOpen}
        onOpenChange={handleOpenChange}
        multiple={multiple}
        disabled={disabled}
        name={name}
      >
        {trigger ? (
          <BaseSelect.Trigger render={trigger} aria-label={ariaLabel} />
        ) : (
          <StyledTrigger
            $size={size}
            $state={state}
            aria-label={ariaLabel}
            data-applied={
              triggerLabel &&
              (Array.isArray(currentValue) ? currentValue.length > 0 : currentValue != null)
                ? true
                : undefined
            }
          >
            {startIcon && <StyledIconWrapper>{startIcon}</StyledIconWrapper>}
            <StyledValue $size={size}>
              {(currentSelectValue: string | string[] | null) => {
                const selectedLabels = getSelectedLabels(optionsByValue, currentSelectValue);
                if (triggerLabel) {
                  return (
                    <>
                      <StyledValueText>{triggerLabel}</StyledValueText>
                      {selectedLabels.length > 0 && (
                        <StyledBadgeWrapper>
                          <Tooltip title={selectedLabels.join(', ')}>
                            <span>
                              <Badge
                                label={String(selectedLabels.length)}
                                color="primary"
                                size="sm"
                              />
                            </span>
                          </Tooltip>
                        </StyledBadgeWrapper>
                      )}
                    </>
                  );
                }
                const [firstLabel, ...restLabels] = selectedLabels;
                return (
                  <>
                    <StyledValueText>{firstLabel ?? placeholder}</StyledValueText>
                    {restLabels.length > 0 && (
                      <StyledBadgeWrapper>
                        <Tooltip title={restLabels.join(', ')}>
                          <span>
                            <Badge label={`+${restLabels.length}`} color="primary" size="sm" />
                          </span>
                        </Tooltip>
                      </StyledBadgeWrapper>
                    )}
                  </>
                );
              }}
            </StyledValue>
            <StyledChevronIcon>
              <ChevronDownIcon />
            </StyledChevronIcon>
          </StyledTrigger>
        )}

        <BaseSelect.Portal>
          <StyledPositioner sideOffset={8} align={align} alignItemWithTrigger={false}>
            <StyledPopup
              style={popupWidth ? { width: popupWidth, maxWidth: popupWidth } : undefined}
            >
              {showSearch && (
                <StyledSearchWrapper>
                  <Input
                    size="md"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder={searchPlaceholder}
                    startIcon={<SearchIcon />}
                  />
                </StyledSearchWrapper>
              )}

              {multiple && showSelectAll && (
                <>
                  <StyledActionsRow>
                    <Button
                      variant="ghost"
                      color="primary"
                      size="sm"
                      onClick={handleSelectAll}
                      disabled={isAllSelected || selectableValues.length === 0}
                    >
                      Select All
                    </Button>
                    <Button
                      variant="ghost"
                      color="primary"
                      size="sm"
                      onClick={handleClear}
                      disabled={isNoneSelected}
                    >
                      Clear
                    </Button>
                  </StyledActionsRow>
                  <StyledSeparator />
                </>
              )}

              <StyledList>
                {ungroupedOptions.map(renderOption)}

                {groupKeys.map((key) => {
                  const cfg = groups[key];
                  const groupHidden = groupedOptions[key].every((o) => hiddenValues.has(o.value));

                  return (
                    <StyledGroup key={key} style={groupHidden ? { display: 'none' } : undefined}>
                      {cfg?.label && (
                        <>
                          <StyledGroupLabel $indented={cfg.indented}>{cfg.label}</StyledGroupLabel>
                          <StyledGroupSeparator />
                        </>
                      )}
                      {groupedOptions[key].map(renderOption)}
                    </StyledGroup>
                  );
                })}
              </StyledList>

              {!hasVisibleOptions && (
                <StyledEmpty role="status">
                  {options.length === 0 ? emptyMessage : noResultsMessage}
                </StyledEmpty>
              )}
            </StyledPopup>
          </StyledPositioner>
        </BaseSelect.Portal>
      </BaseSelect.Root>
      <HelperDescription state={state} iconStart={iconStart} label={helperText} />
    </StyledRoot>
  );
};

Select.displayName = 'Select';

export default Select;
export { default as SelectTriggerButton } from './SelectTriggerButton';
export type {
  SelectGroupConfig,
  SelectMultipleProps,
  SelectOption,
  SelectProps,
  SelectSingleProps,
  SelectSize,
  SelectTriggerButtonProps,
} from './types';
